import i18n from 'i18next';
import { type CountryCodes, type CurrencyCodes, formatCurrency } from 'src/shared/utils/currency';
import { emailRegex, nieRegex, nifRegex, nameRegex, validateOnlyDigitsForPersonalId } from '../../shared/utils/regEx';
import { asIban } from 'src/shared/utils/validators';
import { type DOCUMENT_TYPE, type ErrorFeedback, type ValidatorFeedback } from './types';

export const isEmailValid = (email: string): boolean => emailRegex.test(email);

const trimAndGetLength = (value: string): number => value.trim().length;

export const asName = (value: string, key: string): ValidatorFeedback => {
  const nameLength = trimAndGetLength(value);
  if (nameLength < 1 || nameLength > 100) {
    return {
      error: i18n.t('feedback.errors.the key must have a minimum of min character and a maximum of max', {
        ns: ['employee'],
        key,
        min: '1',
        max: '100'
      })
    };
  }
  if (value[0] === value[0].toLowerCase()) {
    return {
      suggestion: `${i18n.t('common:capitalize', {
        value: i18n.t(`field.${key}`, {
          ns: 'form'
        })
      })} ${i18n.t('feedback.suggestions.does not include an initial capital letter', { ns: ['employee'] })}`
    };
  }

  if (!nameRegex.test(value)) {
    return {
      suggestion: i18n.t('feedback.suggestions.we have found a symbol in the key', {
        ns: ['employee'],
        key
      })
    };
  }

  return false;
};

export const asCodiceFiscale = (id: string): ErrorFeedback | boolean => {
  const personalId = id.trim().toUpperCase();
  const codiceFiscaleRegex = /^[A-Za-z]{6}[0-9]{2}[A-Za-z]{1}[0-9]{2}[A-Za-z]{1}[0-9]{3}[A-Za-z]{1}$/;

  if (personalId.length !== 16) {
    return {
      error: i18n.t('feedback.errors.Codice Fiscale must be 16 characters', {
        ns: ['employee']
      })
    };
  }

  if (!codiceFiscaleRegex.test(personalId)) {
    return {
      error: i18n.t('feedback.errors.Codice Fiscale is not valid', {
        ns: ['employee']
      })
    };
  }

  return false;
};

export const asPortuguesePersonalId = (id: string): ErrorFeedback | boolean => {
  if (id.trim() === '') {
    return {
      error: i18n.t('feedback.errors.DNI/NIE is invalid', { ns: ['employee'] })
    };
  }

  return false;
};

export const asPortuguesePhoneNumber = (phoneNumber: string): ErrorFeedback | boolean => {
  const phoneNumberLength = trimAndGetLength(phoneNumber);
  if (phoneNumber && (phoneNumberLength < 7 || phoneNumberLength > 12)) {
    return {
      error: i18n.t('feedback.errors.phone is invalid', { ns: ['employee'] })
    };
  }

  if (phoneNumber.startsWith('349') || phoneNumber.startsWith('+349')) {
    return {
      error: i18n.t('feedback.errors.you have entered a landline but we require a mobile phone', { ns: ['employee'] })
    };
  }

  return false;
};

export const asFrenchPersonalId = (id: string): ErrorFeedback | boolean => {
  if (id.trim() === '') {
    return {
      error: i18n.t('messages.field is required', { ns: ['form'] })
    };
  }

  return false;
};

const FRENCH_PHONE_REGEX = /^(\+33|0033)?0?[1-9]\d{8}$/;

export const asFrenchPhoneNumber = (phoneNumber: string): ErrorFeedback | boolean => {
  if (phoneNumber && !FRENCH_PHONE_REGEX.test(phoneNumber.replace(/ /g, ''))) {
    return {
      error: i18n.t('feedback.errors.phone is invalid', { ns: ['employee'] })
    };
  }

  return false;
};

export const asFrenchIban = (iban: string): ValidatorFeedback => {
  if (!isNaN(Number(iban.substring(0, 2)))) {
    return {
      error: i18n.t('feedback.errors.iban is invalid', { ns: ['employee'] })
    };
  }

  return asIban(iban);
};

export const asColombianId = (id: string, documentType: DOCUMENT_TYPE): ErrorFeedback | boolean => {
  const personalId = id.trim();

  if (!validateOnlyDigitsForPersonalId.test(personalId) || (documentType === 'PEP' && personalId.length !== 15)) {
    return {
      error: i18n.t('feedback.errors.DNI/NIE is invalid', { ns: ['employee'] })
    };
  }

  return false;
};

export const asPeruId = (id: string): ErrorFeedback | boolean => {
  const personalId = id.trim();

  if (personalId.length > 11 || personalId.length < 4) {
    return {
      error: i18n.t('feedback.errors.DNI/NIE is invalid', { ns: ['employee'] })
    };
  }

  return false;
};

export const asPersonalId = (id: string): ValidatorFeedback => {
  let personalId = id.trim().toUpperCase();

  if (personalId.length < 8 || personalId.length > 9) {
    return {
      error: i18n.t('feedback.errors.DNI/NIE must contain between 7 and 8 characters', { ns: ['employee'] })
    };
  }
  const isNie = ['X', 'Y', 'Z'].includes(personalId[0]);
  const hasControlChar = isNaN(Number(personalId[personalId.length - 1]));
  if (isNie) {
    if (!hasControlChar && personalId.length === 9) {
      personalId += getDniControlChar(personalId.replace('X', '0').replace('Y', '1').replace('Z', '2'));
      return {
        suggestion: `${i18n.t('feedback.suggestions.the NIE is missing the letter', {
          ns: ['employee']
        })} ${personalId}`
      };
    }
  } else if (!hasControlChar && personalId.length === 8) {
    personalId += getDniControlChar(personalId);
    return {
      suggestion: `${i18n.t('feedback.suggestions.the NIE is missing the letter', {
        ns: ['employee']
      })} ${personalId}`
    };
  }

  if (!nifRegex.test(personalId) && !nieRegex.test(personalId)) {
    return {
      error: i18n.t('feedback.errors.DNI/NIE is invalid', { ns: ['employee'] })
    };
  }
  return false;
};

export const asEmail = (email: string, maxLen?: number): ErrorFeedback | boolean => {
  if (email) {
    const maybeValidEmail = isEmailValid(email);
    if (!maybeValidEmail) {
      return {
        error: i18n.t('feedback.errors.email is invalid', { ns: ['employee'] })
      };
    }
  }
  if (maxLen) {
    const error = asMaxLength(email, maxLen);
    if (error) {
      return { error };
    }
  }

  return false;
};

export const asPhoneNumber = (phoneNumber: string): ErrorFeedback | boolean => {
  const phoneNumberLength = trimAndGetLength(phoneNumber);
  if (phoneNumber && (phoneNumberLength < 7 || phoneNumberLength > 13)) {
    return {
      error: i18n.t('feedback.errors.phone is invalid', { ns: ['employee'] })
    };
  }
  if (phoneNumber.startsWith('349') || phoneNumber.startsWith('+349') || phoneNumber.startsWith('9')) {
    return {
      error: i18n.t('feedback.errors.you have entered a landline but we require a mobile phone', { ns: ['employee'] })
    };
  }

  return false;
};

export const asSalary = (salary: string, locale?: CountryCodes, currency?: CurrencyCodes): ValidatorFeedback => {
  const salaryAmount = parseFloat(salary);
  const formatCompanyCurrency = formatCurrency(locale, currency);

  if (Number.isNaN(salaryAmount) || salaryAmount < 0) {
    return {
      error: i18n.t('feedback.errors.the wage should only be a positive number', {
        ns: ['employee']
      })
    };
  }
  if (salaryAmount < 100) {
    return {
      suggestion: i18n.t('feedback.suggestions.the monthly amount is less than amount', {
        ns: ['employee'],
        amount: formatCompanyCurrency(100)
      })
    };
  }
  if (salaryAmount > 4000) {
    return {
      suggestion: i18n.t('feedback.suggestions.the monthly amount is greater than amount', {
        ns: ['employee'],
        amount: formatCompanyCurrency(4000)
      })
    };
  }

  return false;
};

export const asWorkingTimePercentage = (value: number | null | undefined | string): ErrorFeedback | false => {
  if (value === null || value === undefined || value === '') {
    return false;
  }

  const numValue = Number(value);
  if (Number.isNaN(numValue) || numValue <= 0 || numValue > 1) {
    return {
      error: i18n.t('feedback.errors.working time percentage must be greater than 0', {
        ns: ['employee']
      })
    };
  }

  return false;
};

export const asMaxLength = (value: string, max: number): string | false => {
  if (value?.length > max) {
    return `${i18n.t('feedback.errors.must be less than max', {
      ns: ['employee'],
      max
    })}`;
  }
  return false;
};

type ModulusChar = Record<number, string>;

const getDniControlChar = (dni: string) => {
  const modulusChar: ModulusChar = {
    0: 'T',
    1: 'R',
    2: 'W',
    3: 'A',
    4: 'G',
    5: 'M',
    6: 'Y',
    7: 'F',
    8: 'P',
    9: 'D',
    10: 'X',
    11: 'B',
    12: 'N',
    13: 'J',
    14: 'Z',
    15: 'S',
    16: 'Q',
    17: 'V',
    18: 'H',
    19: 'L',
    20: 'C',
    21: 'K',
    22: 'E'
  };
  const parsedDni = parseInt(dni, 10) % 23;

  return modulusChar[parsedDni] || '';
};
